import {useState, useEffect, useMemo} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {getNotesForCustomAddress} from 'service/JobService';
import {genericMessageHandler, usePromise} from 'shared/helpers';
import {useNavigate, useParams} from 'react-router-dom';
import {
    useGetJobStatusStatisticsQuery,
    useJobSaveMutation,
    useLazyGetJobQuery,
} from 'components/customer/Job/store/jobApi';
import {JobStatus, JobStatusFilter} from 'shared/JobStatus';
import {getCreateJobRedirect} from 'components/customer/Job/helpers/getCreateJobRedirect';

export const DispatchMethod = {
    PICKUP: 0,
    FREIGHT_DELIVERY_ADDRESS: 2,
    FREIGHT_CUSTOM_ADDRESS: 1,
    FREIGHT_TO_CLIENT_ADDRESS: 3,
};

export const useFreightNotes = (
    dispatchMethod = DispatchMethod.FREIGHT_CUSTOM_ADDRESS
) => {
    const {notify} = useNotificationContext();
    const [type, setType] = useState(dispatchMethod);
    const [notes, setNotes] = useState([]);

    useEffect(() => {
        const promise = getNotesForCustomAddress(true);

        return usePromise(
            ([notes]) => {
                setNotes(notes);
            },
            [promise],
            (error) => {
                genericMessageHandler(notify, error);
            }
        );
    }, [type]);

    return {setType, notes};
};

export const useJobFormSubmitHandler = (
    jobId,
    currentRoomId,
    noNavigate = false
) => {
    const {notify} = useNotificationContext();
    const {userProfile} = useAppContext();
    const navigate = useNavigate();

    const [saveJob, {isLoading}] = useJobSaveMutation();
    const [getJobDetails] = useLazyGetJobQuery();
    const {room} = useJobContext();

    const fieldMap = {
        jobName: 'job_name',
        jobRefCode: 'job_delivery_name',
        contactNumber: 'job_contact_number',
        description: 'job_description',
        dispatch: 'job_dispatch',
        city: 'job_city',
        street: 'job_address',
        suburb: 'job_suburb',
        postcode: 'job_postcode',
        state: 'job_country_State',
        client: 'job_client_id',
        files: 'job_attachments',
        depotId: 'depot_id',
        set_default_address: 'set_default_address',
        save_address: 'save_address',
        requestedDeliveryDate: 'requested_delivery_date',
    };

    const emptyValues = {
        set_default_address: false,
        save_address: false,
    };

    const jobFormSubmitHandler = async (values) => {
        const formData = new FormData();

        Object.keys(fieldMap).forEach((key) => {
            if (key === 'files') {
                values[String(key)].forEach((file) => {
                    formData.append(`${fieldMap[String(key)]}[]`, file);
                });

                return;
            }
            const value = values[String(key)];

            if (value != null) {
                formData.append(fieldMap[String(key)], value);
            } else {
                formData.append(
                    fieldMap[String(key)],
                    emptyValues[String(key)] ?? ''
                );
            }
        });

        try {
            const response = await saveJob({data: formData, jobId});

            if (response?.error) {
                const {data} = response.error;

                throw new Error(data?.error);
            }

            const {displayId, jobId: jobIdDb, messages} = response.data;

            genericMessageHandler(notify, {message: messages}, 'success');

            let roomId;
            if (jobId === undefined) {
                const {data} = await getJobDetails({
                    jobId: parseInt(jobIdDb),
                });
                roomId = data?.rooms?.[0]?.id;
            }

            // the current room only applies when editing a job, new
            // job without rooms lands on dashboard page
            if (!roomId && jobId !== undefined) {
                roomId = currentRoomId || room?.id;
            }

            if (displayId && !noNavigate) {
                navigate(getCreateJobRedirect(userProfile, displayId, roomId));
            }

            return true;
        } catch (error) {
            genericMessageHandler(notify, [
                'Something went wrong while creating your job. Please trying logging out and logging back in.',
                error.message,
            ]);

            return false;
        }
    };

    return {
        jobFormSubmitHandler,
        loadingSave: isLoading,
    };
};

export const useJobListData = () => {
    const {status} = useParams();
    const [sortBy, setSortBy] = useState([9]);
    const {userProfile} = useAppContext();
    const [orderBy, setOrderBy] = useState('desc');
    const [order, setOrder] = useState({
        fields: [9],
        order: 'desc',
    });

    const {data: jobStatistics, isLoading} = useGetJobStatusStatisticsQuery();

    const allJobStatus = useMemo(() => {
        return [
            {id: 'all', url: 'all', text: 'All'},
            ...JobStatusFilter.filter(
                (status) => !jobStatistics || jobStatistics?.includes(status.id)
            ).map((filter) => {
                const filterFound = userProfile?.jobStatuses?.find(
                    (jobStatus) => filter.status === jobStatus.status
                );
                return {
                    ...filter,
                    text:
                        !!filterFound && filterFound?.custom_label
                            ? filterFound.custom_label
                            : JobStatus[filter.status],
                };
            }),
        ];
    }, [JobStatusFilter, jobStatistics, userProfile?.jobStatuses]);

    const selectedStatus = useMemo(() => {
        let selectedStatusOption = allJobStatus.find(
            (statusOption) => statusOption.url == status
        );

        if (!selectedStatusOption) {
            selectedStatusOption = allJobStatus[0];
        }

        return selectedStatusOption;
    }, [status, allJobStatus]);

    useEffect(() => {
        setOrder((prevState) => ({
            ...prevState,
            fields: sortBy,
            order: orderBy,
        }));
    }, [sortBy, orderBy]);

    return {
        allJobStatus,
        sortBy,
        orderBy,
        order,
        setOrderBy,
        setSortBy,
        selectedStatus,
        userProfile,
        isLoading,
    };
};
